import { Component, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { PageBackService } from '../../services/page-back.service';
import { PlatformService } from '../../services/platform.service';
import { hasDailyLoop } from '../../services/daily-loop';
import { SessionService } from '../../services/session.service';

const LATEST_KEY = (uid: number) => `mk_latest_scan_${uid}`;
function readCachedGuid(uid: number): string | null {
  try {
    return localStorage.getItem(LATEST_KEY(uid));
  } catch {
    return null;
  }
}
function writeCachedGuid(uid: number, guid: string | null): void {
  try {
    if (guid) localStorage.setItem(LATEST_KEY(uid), guid);
    else localStorage.removeItem(LATEST_KEY(uid));
  } catch {}
}

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <nav class="bottom-nav">
      <div class="bn-inner">
        <!-- Back: a real affordance for iOS (no system back). It is shown only
             where it actually goes somewhere. When it is not there the row
             holds no empty slot: the remaining tabs share the width evenly
             (Danna, 17.09). -->
        @if (showBack()) {
          <button
            class="bn-tab bn-back"
            [class.disabled]="!canGoBack()"
            [disabled]="!canGoBack()"
            (click)="back()"
            [attr.aria-label]="'shell.nav.back' | translate"
          >
            <svg class="bn-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span>{{ 'shell.nav.back' | translate }}</span>
          </button>
        }

        <!-- Surface split (Danna 25.09): the daily loop lives in the app, so the
             web shows My results / New scan / Profile only; the app keeps
             Home / My results / New scan / Tools / Profile. My results is hidden
             entirely until she has a completed scan. -->
        @if (dailyLoop) {
          <a class="bn-tab" [class.active]="isActive('home')" (click)="go('home')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span>{{ 'shell.nav.home' | translate }}</span>
          </a>
        }
        @if (latestGuid) {
          <a class="bn-tab" [class.active]="isActive('results/')" (click)="openResults()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-7" /><path d="M22 20H2" />
            </svg>
            <span>{{ 'shell.nav.results' | translate }}</span>
          </a>
        }
        <!-- New scan. Face-in-frame icon (not "+", not QR). Raised magenta disc ONLY
             when active (on new-test); otherwise a flat grey tab like the rest.
             Inert while already on new-test so a mis-tap can't reset a test in progress. -->
        <button class="bn-scan" [class.active]="isActive('new-test')" (click)="scan()" [attr.aria-label]="'shell.nav.scan' | translate">
          <span class="bn-scan-disc">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <!-- viewfinder corners -->
              <path d="M4 8V6a2 2 0 0 1 2-2h2" /><path d="M16 4h2a2 2 0 0 1 2 2v2" />
              <path d="M20 16v2a2 2 0 0 1-2 2h-2" /><path d="M8 20H6a2 2 0 0 1-2-2v-2" />
              <!-- face -->
              <circle cx="12" cy="11" r="2.4" />
              <path d="M8.4 16.2a3.8 3.8 0 0 1 7.2 0" />
            </svg>
          </span>
          <span class="bn-scan-label">{{ 'shell.nav.scan' | translate }}</span>
        </button>

        @if (dailyLoop) {
          <a class="bn-tab" [class.active]="isActive('tools')" (click)="go('tools')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
            </svg>
            <span>{{ 'shell.nav.tools' | translate }}</span>
          </a>
        }

        <a class="bn-tab" [class.active]="isActive('user-account')" (click)="go('user-account')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
          </svg>
          <span>{{ 'shell.nav.profile' | translate }}</span>
        </a>
      </div>
    </nav>
  `,
  styles: [
    `
      .bottom-nav {
        /* The native Android activity already keeps the WebView above the
           system navigation bar. Other edge-to-edge surfaces still need the
           CSS safe area inside the bar. */
        --bottom-nav-safe-area: env(safe-area-inset-bottom, 0px);
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        height: calc(58px + var(--bottom-nav-safe-area));
        background: rgba(255, 255, 255, 0.97);
        backdrop-filter: blur(20px);
        border-top: 1px solid #eee;
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding-top: 7px;
        z-index: 9000;
      }
      :host-context(.native-android) .bottom-nav {
        --bottom-nav-safe-area: 0px;
      }
      .bn-inner {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;
        max-width: 480px;
      }
      /* Every tab takes the same share of the row, so four tabs sit as evenly
         as five and nothing is pushed to one side when Back is absent. */
      .bn-inner > * {
        flex: 1 1 0;
        min-width: 0;
      }
      .bn-tab {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        gap: 2px;
        color: var(--text-muted);
        font-size: 10px;
        font-weight: 500;
        text-align: center;
        cursor: pointer;
        /* B3: >=44px tall touch target. The 22px icon and 10px label are
           untouched; only the tappable box around them grows. */
        min-height: 44px;
        min-width: 44px;
        /* Six tabs (Back + five) on a 360-390px phone leave ~60px each: side
           padding would push "My results" onto two lines (Danna, 13.10). */
        padding: 0 2px;
        white-space: nowrap;
        background: none;
        border: none;
        font-family: inherit;
        -webkit-tap-highlight-color: transparent;
      }
      .bn-tab.active {
        color: var(--brand-text-safe);
      }
      .bn-tab span {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .bn-tab svg {
        width: 22px;
        height: 22px;
      }
      /* Back tab: dimmed + non-interactive at a root / when no history. */
      /* Disabled UI text is exempt from the AA contrast minimum (WCAG 1.4.3
         "Incidental"), but #cfc8c2 was 1.65:1 - all but invisible. This still
         reads clearly as dimmed next to the enabled --text-muted tabs. */
      .bn-back.disabled {
        /* B10: the spec names disabled text explicitly, so this meets AA (4.54:1)
           while still reading dimmer than the enabled tabs (5.33:1). */
        color: #767676;
        cursor: default;
        pointer-events: none;
      }
      /* In RTL the back chevron should point the other way. */
      :host-context([dir='rtl']) .bn-back-icon {
        transform: scaleX(-1);
      }

      /* "New scan" — always a raised round disc: SAME size, icon and shape whether
         active or not. GREY by default, brand-magenta only when active. No focus
         outline (the keyboard ring rendered as a stray black box around the disc). */
      .bn-scan {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        color: var(--text-muted);
        font-size: 10px;
        font-weight: 600;
        background: none;
        border: none;
        padding: 0 2px;
        cursor: pointer;
        font-family: inherit;
        outline: none;
        -webkit-tap-highlight-color: transparent;
      }
      .bn-scan:focus,
      .bn-scan:focus-visible {
        outline: none;
      }
      .bn-scan-disc {
        position: relative;
        width: 50px;
        height: 50px;
        /* The label has to land on the SAME line as the other three tabs, whose
           icon box is 22px tall. The disc is 50px, so it has to give back
           50-22=28px - anything less and the label either sits lower than the
           others or, if pulled up by hand, runs UNDER the disc. */
        margin-top: -28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        /* B10 1.4.11: the disc IS the scan control and the white glyph inside
           it is its only label, so the fill has to clear 3:1 both against the
           white bar behind it and against that glyph. #a89f97 was 2.6:1 on both. */
        background: #8d8177;
        border: 3px solid #fff;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.14);
      }

      .bn-scan-disc svg {
        width: 26px;
        height: 26px;
        color: #fff;
      }
      .bn-scan-label {
        white-space: nowrap;
      }

      /* ACTIVE: same disc, brand-magenta fill + glow, brand label. */
      .bn-scan.active {
        color: var(--brand-text-safe);
      }
      .bn-scan.active .bn-scan-disc {
        background: var(--brand-color);
        box-shadow: 0 6px 16px color-mix(in srgb, var(--brand-color) 45%, transparent);
      }

      /* Desktop (≥600px): the SAME nav moves to the TOP. The raised disc, instead of
         poking ABOVE the bar (off-screen at the top), HANGS BELOW it. */
      @media (min-width: 600px) {
        .bottom-nav {
          top: 0;
          bottom: auto;
          height: 58px;
          border-top: none;
          border-bottom: 1px solid #eee;
          overflow: visible;
        }
        .bn-tab,
        .bn-scan {
          flex-direction: column-reverse;
        }
        .bn-scan-disc {
          margin-top: 6px;
        }
      }
    `,
  ],
})
export class BottomNavComponent {
  private router = inject(Router);
  private translation = inject(TranslationService);
  private location = inject(Location);
  private pageBack = inject(PageBackService);
  private session = inject(SessionService);
  readonly isNative = inject(PlatformService).isNative;
  /** Home + Tools tabs: the app, and Peritale web (no app yet). */
  readonly dailyLoop = hasDailyLoop();
  url = this.router.url;
  /** Her latest completed scan; null hides the My results tab. */
  latestGuid: string | null = null;
  private lastLookupAt = 0;
  private lookupUserId: number | null = null;
  // In-app navigation count this session — guards the back button from walking
  // off the first page (which would leave the app on native / iOS).
  private navCount = 0;

  constructor() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.url = (e as NavigationEnd).urlAfterRedirects;
        this.navCount++;
        this.refreshLatest();
      });
    // The session is rebuilt async on a cold load; look again once it lands.
    try {
      this.session.session$.subscribe(() => this.refreshLatest());
    } catch {}
    this.refreshLatest();
  }

  /** Re-resolve her latest completed scan (the same resolution the landing
   *  uses). Throttled so tab hopping does not refetch on every navigation;
   *  re-runs at once when the signed-in account changes. */
  private refreshLatest(): void {
    // Only with a live app session: an early call without a token would 401.
    const uid = this.session.session?.token ? this.session.session?.user?.id ?? null : null;
    if (!uid) {
      this.latestGuid = null;
      this.lookupUserId = null;
      return;
    }
    // Show the tab at once from the last scan this device resolved for her,
    // instead of waiting on the network (Danna: in the app the tab only
    // appeared once her results had loaded). The lookup below confirms it.
    if (!this.latestGuid) this.latestGuid = readCachedGuid(uid);
    const now = Date.now();
    // Only a FOUND scan is throttled: an empty answer can be a lookup that
    // raced the session restore, so it is asked again on the next navigation.
    if (uid === this.lookupUserId && this.latestGuid && now - this.lastLookupAt < 20000) return;
    this.lookupUserId = uid;
    this.lastLookupAt = now;
    void this.session.getLatestCompletedScanGuid(uid).then((g) => {
      if (this.lookupUserId !== uid) return;
      this.latestGuid = g;
      writeCachedGuid(uid, g);
    });
  }

  openResults(): void {
    // Navigate at once with the scan we already resolved: waiting on a fresh
    // lookup made the tab feel dead (Danna: several taps before it reacts).
    // The lookup still refreshes in the background for the next tap.
    const guid = this.latestGuid;
    if (!guid) return;
    const target = '/' + this.lang() + '/app/results/' + guid;
    if (!this.url.startsWith(target)) this.router.navigateByUrl(target);
    this.lastLookupAt = 0;
    this.refreshLatest();
  }

  private lang(): string {
    return this.translation.getCurrentLanguage();
  }

  isActive(path: string): boolean {
    return this.url.includes('/app/' + path);
  }

  // Root tabs have nothing to go back to.
  private isRoot(): boolean {
    return (
      this.isActive('home') ||
      this.isActive('tools') ||
      this.isActive('results/') ||
      this.isActive('user-account')
    );
  }

  canGoBack(): boolean {
    return !this.isRoot();
  }

  /**
   * B3: never two backs to the same destination.
   * - A shell tool screen (/app/tool/<id>) renders its own header back that goes
   *   to the tools catalogue, exactly where this tab's history-back would land.
   * - Any other page that renders its OWN history back registers with
   *   PageBackService, so this tab steps aside there too (widget-settings, the
   *   full-results symptoms view) without the nav having to know the routes.
   */
  showBack(): boolean {
    if (/\/app\/tool\//.test(this.url)) return false;
    // A tab that is there but does nothing reads as broken (Danna, 17.09). On a
    // root tab there is nowhere to go back to, so the slot holds the spacer and
    // the other tabs keep their places, instead of a dimmed inert button.
    if (!this.canGoBack()) return false;
    return !this.pageBack.hasPageBack;
  }

  back(): void {
    if (!this.canGoBack()) return;
    // If this is the first page we've seen, there's no in-app history to pop —
    // fall back to home rather than risk exiting the app.
    if (this.navCount <= 1) {
      this.go('home');
      return;
    }
    this.location.back();
  }

  go(path: string): void {
    this.router.navigate(['/' + this.lang() + '/app/' + path]);
  }

  scan(): void {
    // Don't re-navigate (which would reset the form) if a scan is already open.
    if (this.isActive('new-test')) return;
    this.router.navigate(['/' + this.lang() + '/app/new-test']);
  }
}
